import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "reactstrap";
import { queryNoTracking, update } from "../services/DataService";
import userService, { getUser } from "../services/UserService";
import { Helper } from "../services/JobService";
import { t } from '../services/HelperService';
import { buildWhatsAppPropsForChat } from "../services/WhatsAppService";
import { AccentPopup } from "../../components/dialogs/AccentPopup";
import { AccentSpinner } from "../controls/AccentSpinner";
import AccentIcons from '../icons/Icons';
import { CustomerChatToolbar } from "../controls/customerChat/CustomerChatToolbar";
import { BsChatDotsFill } from "react-icons/bs";
import { CustomerChatList } from "../controls/customerChat/CustomerChatList";
import { CustomerChatRightPane } from "../controls/customerChat/CustomerChatRightPane";
import AssigneeActions from "../controls/customerChat/AssigneeActions";
import UseBreakpoint from "../controls/customerChat/hooks/UseBreakpoint";
import UseMobileRowWidth from "../controls/customerChat/hooks/UseMobileRowWidth";
import UseChatUpdates from "../controls/customerChat/hooks/UseChatUpdates";
import { CHANNEL_OPTIONS, STATUS_OPTIONS } from "../controls/customerChat/ChatOptions";
import { OpenNewContactForChat } from "../controls/chat/helpers/OpenNewContactForChat";
import { OpenNewLeadForChat } from "../controls/chat/helpers/OpenNewLeadForChat";
import { OpenLinkJobsDlg } from "../controls/chat/helpers/OpenLinkJobsDlg";
import { OpenNewChatDlg } from "../controls/chat/helpers/OpenNewChatDlg";

// --- Data queries ------------------------------------------------------------
const query_ChatAll = queryNoTracking("Chat_All");
const query_ChatMessages = queryNoTracking("Chat_Messages");
const query_ChatGetChannelConfiguration = queryNoTracking("Chat_GetChannelConfiguration");

// --- Helpers -----------------------------------------------------------------
const Channel = {
    WhatsApp: "whatsapp",
    SMS: "sms",
    Email: "email",
};

export default function CustomerChatView({ initialFilters }) {
    // --- Fixed refs -----------------------------------------------------------
    const splitRef = useRef(null);
    const chatDlgRef = useRef(null);
    const selectedRef = useRef(null);
    const pendingStatusRef = useRef({ chatId: null, until: 0 });
    const mobileListRef = useRef(null);

    // --- Local UI state (layout / list / filters / selection / user / rhs) ----
    const [leftWidth, setLeftWidth] = useState(() => {
        const saved = Number(localStorage.getItem("ccv.leftWidth"));
        return Number.isFinite(saved) && saved >= 240 ? saved : 360;
    });
    const latestWidthRef = useRef(leftWidth);

    const MOBILE_BP = 992;
    const isMobile = UseBreakpoint(MOBILE_BP);
    const [mobileMode, setMobileMode] = useState("list");
    const [uniqPopup, setUniqPopup] = useState({ show: false, anchor: null });

    const [isListLoading, setIsListLoading] = useState(false);
    const [isRhsSwitching, setIsRhsSwitching] = useState(false);
    const [chats, setChats] = useState([]);
    const [total, setTotal] = useState(0);

    const [dataState, setDataState] = useState({
        skip: 0, take: 40, isArchived: false, status: null, subject: null,
        channel: initialFilters?.channel ?? null,
        contactPoint: initialFilters?.contactPoint ?? null,
        contactId: initialFilters?.contactId ?? null,
        jobId: initialFilters?.jobId ?? null,
        unreadOnly: false
    });
    const [channelValues, setChannelValues] = useState([]);
    const [statusValues, setStatusValues] = useState([]);
    const [chatIdQuery, setChatIdQuery] = useState("");
    const [subjectQuery, setSubjectQuery] = useState("");
    const [showArchived, setShowArchived] = useState(false);
    const [mineOnly, setMineOnly] = useState(false);
    const [unreadOnly, setUnreadOnly] = useState(false);

    const [selected, setSelected] = useState(null);
    const [activeChannel, setActiveChannel] = useState(Channel.WhatsApp);
    const [assigneeOpenChatId, setAssigneeOpenChatId] = useState(null);
    const selectedChatId = selected?.ChatID ?? null;

    const [currentUserName, setCurrentUserName] = useState(null);
    const [currentRepId, setCurrentRepId] = useState(null);
    const [currentUserPrimaryBusinessUnitId, setCurrentUserPrimaryBusinessUnitId] = useState(null);

    const [channelConfig, setChannelConfig] = useState(null);
    const [waProps, setWaProps] = useState(null);

    const [showChannelWarning, setShowChannelWarning] = useState(false);
    const [channelWarningMessage, setChannelWarningMessage] = useState("");

    useEffect(() => { selectedRef.current = selected; }, [selected])

    // --- Once-only init effects -----------------------------------------------
    useEffect(() => {
        const trySet = () => {
            const u = getUser?.();
            if (!u) return false;
            const id =
                u.RepresentativeID ??
                u.RepresentativeId ??
                u.Representative?.ID ??
                u.ID ?? null;
            if (id != null) setCurrentRepId(Number(id));
            const name = u?.FullName || u?.Name || null;
            if (name) setCurrentUserName(name);
            const primaryBusinessUnitId = u?.DefaultSettings?.PrimaryBusinessUnitID;
            if (primaryBusinessUnitId) setCurrentUserPrimaryBusinessUnitId(primaryBusinessUnitId);
            return id != null;
        };
        // try immediately
        if (!trySet()) {
            const key = "CustomerChatView.mineInit";
            userService.registerOnInit(key, () => {
                trySet();
                userService.deRegisterOnInit(key);
            });
            return () => userService.deRegisterOnInit(key);
        }
    }, []);

    useEffect(() => {
        setMobileMode(isMobile ? (selected ? "detail" : "list") : "split");
    }, [isMobile, selected]);

    useEffect(() => {
        (async () => {
            const config = await query_ChatGetChannelConfiguration.getFirstOrDefault();
            setChannelConfig(config);
        })();
    }, []);

    // Check if initial channel filter is disabled
    useEffect(() => {
        if (!channelConfig || !initialFilters?.channel) {
            setShowChannelWarning(false);
            return;
        }

        const requestedChannel = String(initialFilters.channel).toLowerCase();
        let isEnabled = false;
        let channelName = "";

        switch (requestedChannel) {
            case 'whatsapp':
                isEnabled = channelConfig.WhatsAppEnabled;
                channelName = "WhatsApp";
                break;
            case 'sms':
                isEnabled = channelConfig.SmsEnabled;
                channelName = "SMS";
                break;
            case 'email':
                isEnabled = channelConfig.EmailEnabled;
                channelName = "Email";
                break;
            case 'supply':
                isEnabled = channelConfig.SupplyChatEnabled;
                channelName = "Supply Chat";
                break;
        }

        if (!isEnabled && channelName) {
            setShowChannelWarning(true);
            setChannelWarningMessage(t("application_strings.views.chat.banner.channelNotConfiguredMessage", { channelName: channelName}));
        } else {
            setShowChannelWarning(false);
        }
    }, [channelConfig, initialFilters?.channel]);

    // --- Derived sets / selectors ---------------------------------------------
    const selectedIds = React.useMemo(() => {
        return new Set(channelValues.map(v => v.ID).filter(id => id !== "ALL"));
    }, [channelValues]);

    const selectedStatusIds = React.useMemo(() => {
        return new Set(statusValues.map(v => v.ID).filter(id => id !== "ALL"));
    }, [statusValues]);

    const lc = (v) => String(v ?? "").toLowerCase();
    const matchesFilters = (c, term, idQ) => {
        const ch = lc(c.Channel), st = lc(c.Status);
        const matchChannel = selectedIds.size === 0 || selectedIds.has(ch);
        const matchStatus = selectedStatusIds.size === 0 || selectedStatusIds.has(st);
        const matchId = idQ === "" || String(c.ID) === idQ;
        const t = term.trim().toLowerCase();
        const matchText = lc(c.Subject).includes(t) || lc(c.ContactName).includes(t) || lc(c.ContactPoint).includes(t);
        const mineById = currentRepId != null && Number(c.RepresentativeAssigneeID) === Number(currentRepId);
        const mineByName = currentUserName && String(c.RepresentativeAssigneeName || "") === String(currentUserName);
        const matchMine = !mineOnly || mineById || mineByName;
        const matchArchived = Boolean(c.IsArchived) === showArchived;
        return matchChannel && matchStatus && matchId && matchText && matchMine && matchArchived;
    };

    const isSelectedVisible = useCallback((c) => matchesFilters(c, subjectQuery, chatIdQuery.trim()), [
        subjectQuery, chatIdQuery, selectedIds, selectedStatusIds, mineOnly, currentRepId, currentUserName, showArchived
    ]);

    const linkedJobsKey = React.useMemo(
        () => (selected?.LinkedJobs ?? []).map(j => j.JobID ?? j.ID).join('|'),
        [selected?.LinkedJobs]
    );

    // Visible list based on selected IDs
    const visibleChats = useMemo(() => {
        const q = chatIdQuery.trim();
        return chats.filter(c => matchesFilters(c, subjectQuery, q));
    }, [chats, subjectQuery, chatIdQuery, selectedIds, selectedStatusIds, mineOnly, currentRepId, currentUserName, showArchived]);

    const getMessages = useMemo(
        () => (selected?.ChatID
            ? () => query_ChatMessages.getAllAsQueryable({ chatId: selected.ChatID })
            : null),
        [selected?.ChatID]
    );

    // --- Resizing logic -------------------------------------------------------
    const startResize = (e) => {
        if (!splitRef.current) return;
        const container = splitRef.current;
        const startX = e.clientX;
        const startW = leftWidth;
        const min = 240;
        const max = Math.max(min, container.clientWidth - 420);

        const onMove = (ev) => {
            const dx = ev.clientX - startX;
            const next = Math.min(Math.max(startW + dx, min), max);
            latestWidthRef.current = next;     // <-- keep the newest value
            setLeftWidth(next);
        };

        const onUp = () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
            document.body.style.cursor = "";

            const final = latestWidthRef.current ?? leftWidth;
            localStorage.setItem("ccv.leftWidth", String(Math.round(final)));
        };

        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
        document.body.style.cursor = "col-resize";
    };

    // --- Layout & Responsiveness ----------------------------------------------
    const { containerProps: mobileListProps } = UseMobileRowWidth({
        enabled: isMobile && mobileMode === "list",
        rootRef: mobileListRef,
        selector: ".ccv-row-inner"
    });

    // --- Fetching & live updates ----------------------------------------------
    const normalizeChatRow = (r) => ({
        ...r,
        ID: r.ID ?? r.iD ?? r.id,
        ChatID: r.ChatID ?? r.chatID ?? r.chatId,
        Subject: r.Subject ?? r.subject,
        ContactPoint: r.ContactPoint ?? r.contactPoint,
        ContactID: r.ContactID ?? r.contactID ?? r.contactId,
        ContactName: r.ContactName ?? r.contactName,
        Channel: r.Channel ?? r.channel,
        Status: r.Status ?? r.status,
        LastActivityAt: r.LastActivityAt ?? r.lastActivityAt,
        LastUserMessageAt: r.LastUserMessageAt ?? r.lastUserMessageAt,
        LastBusinessMessageAt: r.LastBusinessMessageAt ?? r.lastBusinessMessageAt,
        OpenDate: r.OpenDate ?? r.openDate,
        CloseDate: r.CloseDate ?? r.closeDate,
        IsArchived: r.IsArchived ?? r.isArchived,
        ArchivedAt: r.ArchivedAt ?? r.archivedAt,
        ArchivedByRepresentativeName:
            r.ArchivedByRepresentativeName ?? r.archivedByRepresentativeName,
        RepresentativeAssigneeID:
            r.RepresentativeAssigneeID ?? r.representativeAssigneeID,
        RepresentativeAssigneeName:
            r.RepresentativeAssigneeName ?? r.representativeAssigneeName,
        TeamAssigneeID: r.TeamAssigneeID ?? r.teamAssigneeID,
        TeamAssigneeName: r.TeamAssigneeName ?? r.teamAssigneeName,
        UnreadInboundCount: r.UnreadInboundCount ?? r.unreadInboundCount,
        UnreadMentionCount: r.UnreadMentionCount ?? r.unreadMentionCount,
        IsManuallyUnread: r.IsManuallyUnread ?? r.isManuallyUnread,
        IsPinned: r.IsPinned ?? r.isPinned,
        IsMuted: r.IsMuted ?? r.isMuted,
        CanEdit: r.CanEdit ?? r.canEdit,
        LinkedJobs: r.LinkedJobs ?? r.linkedJobs ?? [],
    });

    const fetchChats = useCallback(async (state = dataState, opts = {}) => {
        const { silent = false } = opts;
        if (!silent) setIsListLoading(true);
        try {
            const getter = query_ChatAll.getAll;
            const result = await getter({
                ...state,
                status: (state.status && state.status.length ? state.status : undefined),
            });
            
            const wrap = Array.isArray(result) ? result : [result];
            const first = wrap[0] ?? [];
            
            const payload = Array.isArray(first)
                ? { data: first, total: first.length }
                : {
                    data: first.data  ?? first.Data ?? [],
                    total: first.total ?? first.Total ?? (Array.isArray(first) ? first.length : 0)
                };
     
            const rows = (payload.data || []).map(normalizeChatRow);
    
            setChats(prev => {
                if ((state?.skip ?? 0) === 0) return rows;
                const merged = [...prev, ...rows];
                const seen = new Set();
                return merged.filter(x => {
                        const key = x?.ChatID;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    });
            });

            setTotal(Number(payload.total ?? rows.length));
            const curSel = selectedRef.current;

            if (curSel) {
                const updated = rows.find(c => c.ChatID === curSel.ChatID);
                if (updated) {
                    setSelected(s => {
                        if (!s) return s;
                        const pending = pendingStatusRef.current;
                        const inHold = pending.chatId === s.ChatID && Date.now() < pending.until;
                        return {
                            ...s,
                            ...updated,
                            Status: inHold ? s.Status : updated.Status,
                            CloseDate: (updated.CloseDate ?? s.CloseDate ?? null)
                        };
                    });
                }
            }
        } catch (err) {
            console.error("Chat_All failed", err);
            setChats([]);
            setTotal(0);
            setSelected(null);
        } finally {
            if (!silent) setIsListLoading(false);
        }
    }, [dataState]);

    useEffect(() => { fetchChats(); }, [fetchChats]);

    UseChatUpdates({
        onRefresh: (silent) => fetchChats(undefined, { silent }),
        onMention: (e) => {
            fetchChats(undefined);
            if (selectedRef.current?.ChatID === e.ConversationID && e.MessageID) {
                setTimeout(() => chatDlgRef.current?.jumpToMessage?.(String(e.MessageID)), 0);
            }
        }
    });

    // --- Channel adapters -----------------------------------------------------
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!selectedChatId || activeChannel !== Channel.WhatsApp) {
                setWaProps(null);
                setIsRhsSwitching(false);
                return;
            }
            setIsRhsSwitching(true);
            setWaProps(null);
            try {
                const props = await buildWhatsAppPropsForChat({
                    chat: selected,
                    preferChatId: true,
                    title: selected?.Subject || t("application_strings.views.chat.channelTitle", {contactPoint: selected?.ContactPoint})
                });
                if (!cancelled) {
                    setWaProps(prev => (prev?.chatID === props?.chatID ? prev : props));
                }
            } catch (e) {
                console.error("Failed to build WA props", e);
                if (!cancelled) setWaProps(null);
            } finally {
                if (!cancelled) setIsRhsSwitching(false);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedChatId, activeChannel, linkedJobsKey]);

    const chatDlgProps = useMemo(() => {
        if (!selected) return null;

        const conversationId = selected.ContactPoint;

        // common funcs
        const markAsRead = async (messageID) => update("Chat_Message_MarkAsRead", { MessageID: Number(messageID) });
        const markAsResent = async (messageID) => update("Chat_Message_MarkAsResent", { MessageID: Number(messageID) });

        if (activeChannel === Channel.WhatsApp) {
            return waProps;
        }

        if (activeChannel === Channel.SMS) {
            const saveMessage = async (message) => {
                await Helper.sendSms(conversationId, message.CustomMessage, selected.ContactID);
                return true;
            };
            const alwaysOpen = async () => ({ IsOpen: true });
            return {
                title: selected.Subject || `SMS with ${selected.ContactPoint}`,
                conversationID: conversationId,
                query: getMessages,
                serviceWindowQuery: alwaysOpen,
                templateQuery: async () => [],
                generateTemplateContent: async () => null,
                saveMessage,
                alternateChannelSaveMessage: [],
                markAsRead,
                markAsResent,
            };
        }

        // TODO: Wire up Email sending later
        if (activeChannel === Channel.Email) {
            const saveMessage = async (message) => {
                console.warn("Email send not wired – implement your Helper.sendEmail here.");
                return true;
            };

            const alwaysOpen = async () => ({ IsOpen: true });

            return {
                title: selected.Subject || `Email with ${selected.ContactPoint}`,
                conversationID: conversationId,
                query: getMessages,
                serviceWindowQuery: alwaysOpen,
                templateQuery: async () => [],
                generateTemplateContent: async () => null,
                saveMessage,
                alternateChannelSaveMessage: [],
                markAsRead,
                markAsResent,
            };
        }

        return null;
    }, [selected, activeChannel, waProps]);

    // --- UI helpers -----------------------------------------------------
    const ChannelIcon = useCallback(({ channel, size = 30, className }) => {
        const key = String(channel ?? "").toLowerCase();
        const common = { style: { width: size, height: size }, className, "aria-label": key };
        switch (key) {
            case "whatsapp": return <AccentIcons.WhatsAppIconOnlyWhiteOnGreenSmall {...common} />;
            case "sms": return <AccentIcons.SMS {...common} />;
            case "email": return <AccentIcons.Email {...common} />;
            default: return null;
        }
    }, []);

    const openNewChat = useCallback(async () => {
        const contactId =
            selected?.ContactID ?? dataState.contactId ?? null;
        const jobId =
            (selected?.LinkedJobs?.[0]?.JobID ?? selected?.LinkedJobs?.[0]?.ID) ??
            dataState.jobId ?? null;
        const contactPoint = selected?.ContactPoint ?? null;
        const channel = activeChannel;
        
        const draft = await OpenNewChatDlg({
            contactId,
            primaryBusinessUnitId: currentUserPrimaryBusinessUnitId,
            jobId,
            channel,
            contactPoint,
            channelConfig
        });

        if (draft.canceled) return;

        if (draft?.redirectChat) {
            const existing = draft.redirectChat;
            setSelected(existing);
            if (String(existing.Channel ?? "").toLowerCase()) {
                setActiveChannel(String(existing.Channel).toLowerCase());
            }
            return;
        }
        
        // Build server payload (CreateChatModel)
        const payload = {
            Channel: draft.channel,
            ContactID: draft.contactID ?? null,
            ContactPoint: draft.contactPoint ?? null,
            Subject: draft.subject ?? "",
            JobID: draft.jobID ?? 0,
            SenderIdentityID: draft.phoneNumberConfigID ?? null,
            Message: {
                Type: "Template",
                TemplateID: draft.templateID,
                CustomMessage: null
            }
        };
    
        const res = await update("Chat_Create", payload);

        await fetchChats(undefined);
        const createdChatId = res?.ChatID ?? res?.chatID ?? res?.chatId ?? null;

        if (createdChatId) {
            const row = (c) => String(c.ChatID) === String(createdChatId);
            const found = (prev => prev.find?.(row))(chats) || null;
            if (found) setSelected(found);
            else {
                const pick = (list) => list.find?.(row);
                const maybe = pick(chats);
                if (maybe) setSelected(maybe);
            }
        }
    }, [selected, dataState.contactId, dataState.jobId, activeChannel, chats, fetchChats, channelConfig]);

    // --- Row actions (mutations) ----------------------------------------------
    const patchChatRow = useCallback((chatId, patch) => {
        setChats(list => list.map(c => (c.ChatID === chatId ? { ...c, ...patch } : c)));
        if (selectedRef.current?.ChatID === chatId) {
            setSelected(s => (s ? { ...s, ...patch } : s));
        }
    }, []);

    const patchSelectedRow = useCallback((patch) => {
        const id = selectedRef.current?.ChatID;
        if (!id) return;
        patchChatRow(id, patch);
    }, [patchChatRow]);

    const setUserState = useCallback(async (chat, patch) => {
        patchChatRow(chat.ChatID, patch);
        try {
            const ok = await update("Chat_UserState_Set", { ChatId: chat.ChatID, ...patch });
            if (!ok) throw new Error("server returned false");
        } catch (e) {
            console.error("Chat_UserState_Set failed", e);
            await fetchChats(undefined);
        }
    }, [fetchChats, patchChatRow]);

    const markChatAsRead = useCallback(async chat => {
        try {
            await update("Chat_Mention_MarkAsRead", { ChatId: chat.ChatID, ChatMessageId: null });
        } finally {
            await setUserState(chat, { IsManuallyUnread: false });
        }
    }, [setUserState]);

    const applyStatus = useCallback(async (chat, newStatus) => {
        const nonClosed = String(newStatus).toLowerCase() !== "closed";
        if (nonClosed) {
            const hasSiblingOpen = chats.some(c =>
                c.ChatID !== chat.ChatID &&
                String(c.Status).toLowerCase() !== "closed" &&
                !c.IsArchived &&
                String(c.Channel).toLowerCase() === String(chat.Channel).toLowerCase() &&
                String(c.ContactPoint) === String(chat.ContactPoint) &&
                String(c.From) === String(chat.From)
            );
            if (hasSiblingOpen) {
                const anchorEl = document.getElementById(`status-${chat.ChatID}`);
                setUniqPopup({ show: true, anchor: { current: anchorEl || splitRef.current } });
                return;
            }
        }

        const prevStatus = chat.Status;
        const prevCloseDate = chat.CloseDate ?? null;
        const willBeClosed = String(newStatus).toLowerCase() === "closed";

        const optimistic = { Status: newStatus, CloseDate: willBeClosed ? new Date().toISOString() : null };

        setChats(list => list.map(c => c.ChatID === chat.ChatID ? { ...c, ...optimistic } : c));
        if (selectedRef.current?.ChatID === chat.ChatID) {
            setSelected(s => s ? { ...s, ...optimistic } : s);
        }

        pendingStatusRef.current = { chatId: chat.ChatID, until: Date.now() + 1500 };

        try {
            const ok = await update("Chat_UpdateStatus", { ChatId: chat.ChatID, Status: newStatus });
            if (!ok) {
                if (nonClosed) {
                    const anchorEl = document.getElementById(`status-${chat.ChatID}`);
                    setUniqPopup({ show: true, anchor: { current: anchorEl } });
                }
                throw new Error("server returned false");
            }
            await fetchChats(undefined);
        } catch (err) {
            console.error("Chat_UpdateStatus failed", err);
            setChats(list => list.map(c =>
                c.ChatID === chat.ChatID ? { ...c, Status: prevStatus, CloseDate: prevCloseDate ?? null } : c
            ));
            if (selectedRef.current?.ChatID === chat.ChatID) {
                setSelected(s => (s ? { ...s, Status: prevStatus, CloseDate: prevCloseDate ?? null } : s));
            }
        }
    }, [chats, fetchChats, splitRef]);

    const setArchive = useCallback(async (chat, nextArchived) => {
        const prev = {
            IsArchived: !!chat.IsArchived,
            ArchivedAt: chat.ArchivedAt ?? null,
            ArchivedByRepresentativeName: chat.ArchivedByRepresentativeName ?? null,
        };

        const optimistic = nextArchived
            ? { IsArchived: true, ArchivedAt: new Date(), ArchivedByRepresentativeName: currentUserName }
            : { IsArchived: false, ArchivedAt: null, ArchivedByRepresentativeName: null };

        setChats(list => list.map(c => c.ChatID === chat.ChatID ? { ...c, ...optimistic } : c));
        if (selectedRef.current?.ChatID === chat.ChatID) {
            setSelected(s => s ? { ...s, ...optimistic } : s);
        }

        try {
            const ok = await update("Chat_Archive", { ChatId: chat.ChatID, IsArchived: nextArchived });
            if (!ok) throw new Error("server returned false");

            await fetchChats(undefined);
        } catch (err) {
            console.error("Chat_Archive failed", err);

            setChats(list => list.map(c => c.ChatID === chat.ChatID ? { ...c, ...prev } : c));
            if (selectedRef.current?.ChatID === chat.ChatID) {
                setSelected(s => s ? { ...s, ...prev } : s);
            }
        }
    }, [currentUserName, fetchChats]);

    const buildMoreActions = useCallback((chat) => {
        const isPinned = !!chat.IsPinned;
        const isMuted = !!chat.IsMuted;
        const isManuallyUnread = !!chat.IsManuallyUnread;
        const hasUnreadMsgs =
            Number(chat.UnreadInboundCount || 0) > 0 ||
            Number(chat.UnreadMentionCount || 0) > 0;
        const needsAttention = hasUnreadMsgs || isManuallyUnread;

        return [
            needsAttention
                ? { text: t("application_strings.views.chat.actions.markRead"), onClick: async () => markChatAsRead(chat) }
                : { text: t("application_strings.views.chat.actions.markUnread"), onClick: async () => setUserState(chat, { IsManuallyUnread: true }) },
            isPinned
                ? { text: t("application_strings.views.chat.actions.unpin"), onClick: async () => setUserState(chat, { IsPinned: false }) }
                : { text: t("application_strings.views.chat.actions.pin"), onClick: async () => setUserState(chat, { IsPinned: true }) },
            isMuted
                ? { text: t("application_strings.views.chat.actions.unmute"), onClick: async () => setUserState(chat, { IsMuted: false }) }
                : { text: t("application_strings.views.chat.actions.mute"), onClick: async () => setUserState(chat, { IsMuted: true }) },
            "divider",
            {
                text: t("application_strings.views.chat.actions.linkJobs"), onClick: async () => {
                    const res = await OpenLinkJobsDlg({
                        chatID: chat.ChatID,
                        chatIntID: chat.ID,
                        chatSubject: chat.Subject || "",
                        contactID: chat.ContactID,
                        linkedJobs: chat.LinkedJobs
                    })
                    if (res) {
                        if (Array.isArray(res.linkedJobs)) {
                            patchChatRow(chat.ChatID, { LinkedJobs: res.linkedJobs });
                        }
                        fetchChats(undefined, { silent: true });
                    }
                }
            },
            // { text: "Manage Links", onClick: async () => setUserState(chat, { IsMuted: false }) }, TODO: Can implement when linking to other entities is complete - can be a page with all current (and maybe past?) links - acts as a central page for managing linking
            "divider",
            ...(chat.ContactID ? [] : [{
                text: t("application_strings.views.chat.actions.newContact"),
                    onClick: async () => {
                        const res = await OpenNewContactForChat({ chatID: chat.ChatID, contactPoint: chat.ContactPoint });
                        if (res) {
                        // keep LHS + RHS in sync
                            patchChatRow(chat.ChatID, { ContactID: res.contactId, ContactName: res.contactName });
                            fetchChats(undefined, { silent: true });
                        }
                    }
            }]),
            {
                text: t("application_strings.views.chat.actions.newLead"), onClick: () => OpenNewLeadForChat({
                    contactID: chat.ContactID,
                    contactPoint: chat.ContactPoint,
                    contactName: chat.ContactName
                })
            },
            "divider",
            chat.IsArchived
                ? { text: t("application_strings.views.chat.actions.unarchive"), onClick: async () => setArchive(chat, false) }
                : { text: t("application_strings.views.chat.actions.archive"), onClick: async () => setArchive(chat, true) },
        ];
    }, [setArchive, markChatAsRead, setUserState]);


    const { onAssignToMe, onAssigneeCommitted } = AssigneeActions({
        chats, selected, setChats, setSelected, fetchChats,
        assigneeOpenChatId, setAssigneeOpenChatId,
        currentUserName, currentRepId,
    });

    // --- Selection -----------------------------------------------------------
    const handleSelect = useCallback((chat) => {
        if (assigneeOpenChatId != null) return;

        setSelected(chat);
        if (chat?.Channel) setActiveChannel(String(chat.Channel ?? "").toLowerCase());
        if (isMobile) setMobileMode("detail");

        if (chat.IsManuallyUnread) {
            setUserState(chat, { IsManuallyUnread: false });
        }
        update("Chat_UserState_TouchLastViewed", { ChatId: chat.ChatID }).catch(() => { });
    }, [assigneeOpenChatId, isMobile]);

    useEffect(() => {
        if (selected && !isSelectedVisible(selected)) setSelected(null);
    }, [selected, isSelectedVisible]);

    // --- Render Flags --------------------------------------------------------
    const layout = isMobile ? "single" : "split";
    const showList = isMobile ? (mobileMode !== "detail") : true;
    const showDetail = isMobile ? (mobileMode === "detail") : true;

    // --- Render --------------------------------------------------------------
    return (
        <div
            ref={splitRef}
            className={`ccv-root ccv-${layout}`}
            style={{ '--left': `${leftWidth}px` }}
        >
            {/* Left list pane */}
            <aside className={`ccv-left ${showList ? '' : 'is-hidden'}`}>
                {isListLoading && <div className="ccv-list-overlay"><AccentSpinner /></div>}

                <CustomerChatToolbar
                    chatIdQuery={chatIdQuery}
                    setChatIdQuery={setChatIdQuery}
                    subjectQuery={subjectQuery}
                    setSubjectQuery={setSubjectQuery}
                    channelValues={channelValues}
                    setChannelValues={setChannelValues}
                    statusValues={statusValues}
                    setStatusValues={setStatusValues}
                    mineOnly={mineOnly}
                    setMineOnly={setMineOnly}
                    showArchived={showArchived}
                    setShowArchived={setShowArchived}
                    unreadOnly={unreadOnly}
                    setUnreadOnly={setUnreadOnly}
                    currentRepId={currentRepId}
                    chats={chats}
                    channelDataSource={async () => CHANNEL_OPTIONS}
                    statusDataSource={async () => STATUS_OPTIONS}
                    setDataState={setDataState}
                    fetchChats={fetchChats}
                    setSelected={setSelected}
                    isMobile={isMobile}
                    onOpenNewChat={openNewChat}
                />

                {/* Channel Configuration Warning Banner */}
                {showChannelWarning && (
                    <div
                        className="alert alert-warning mx-3 mt-2 mb-0 d-flex align-items-start"
                        role="alert"
                        style={{ fontSize: '0.875rem' }}
                    >
                        <i className="fa fa-exclamation-triangle me-2 mt-1" style={{ flexShrink: 0 }}></i>
                        <div style={{ flex: 1 }}>
                            <strong>{t("application_strings.views.chat.banner.channelNotConfigured")}</strong>
                            <div className="mt-1">{channelWarningMessage}</div>
                        </div>
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Dismiss"
                            onClick={() => setShowChannelWarning(false)}
                            style={{ fontSize: '0.75rem' }}
                        ></button>
                    </div>
                )}

                {isMobile ? (
                    <div {...mobileListProps}>
                        <CustomerChatList
                            visibleChats={visibleChats}
                            selected={selected}
                            onSelect={(chat) => {
                                handleSelect(chat);
                                setMobileMode("detail");
                            }}
                            buildMoreActions={buildMoreActions}
                            assigneeOpenChatId={assigneeOpenChatId}
                            setAssigneeOpenChatId={setAssigneeOpenChatId}
                            onAssignToMe={onAssignToMe}
                            onAssigneeCommitted={onAssigneeCommitted}
                            onStatusChange={applyStatus}
                            ChannelIcon={ChannelIcon}
                        />
                    </div>
                ) : (
                    <CustomerChatList
                        visibleChats= {visibleChats}
                        selected={selected}
                        onSelect={handleSelect}
                        buildMoreActions={buildMoreActions}
                        assigneeOpenChatId={assigneeOpenChatId}
                        setAssigneeOpenChatId={setAssigneeOpenChatId}
                        onAssignToMe={onAssignToMe}
                        onAssigneeCommitted={onAssigneeCommitted}
                        onStatusChange={applyStatus}
                        ChannelIcon={ChannelIcon}
                    />
                )}

                <div className="d-flex justify-content-between align-items-center mt-3 px-3">
                    <span className="text-muted small">
                        {total > 0
                            ? t("application_strings.views.chat.paging.range", { shown: Math.min(chats.length, total), total: total })
                            : t("application_strings.views.chat.paging.zero") }
                    </span>
                    <Button
                        onClick={() => setDataState(s => ({ ...s, skip: s.skip + s.take }))}
                        hidden={chats.length >= total}
                    >
                        {t("application_strings.views.chat.paging.loadMore")}
                    </Button>
                </div>

                {/* Mobile FAB: only show on mobile while list is visible */}
                {isMobile && showList && (
                    <div className="ccv-fab-wrap">
                        <button
                            type="button"
                            className="ccv-fab"
                            aria-label="New chat"
                            onClick={openNewChat}
                            title="New chat"
                        >
                            <BsChatDotsFill />
                        </button>
                    </div>
                )}
            </aside>

            {/* Drag handle (desktop only) */}
            {!isMobile && (
                <div className="ccv-resizer" onMouseDown={startResize} role="separator" aria-orientation="vertical" title={t("application_strings.views.chat.a11y.dragToResize")} />
            )}

            {/* Right pane */}
            <main className={`ccv-right ${showDetail ? '' : 'is-hidden'}`}>
                <CustomerChatRightPane
                    ref={chatDlgRef}
                    selected={selected}
                    chatDlgProps={chatDlgProps}
                    isSwitching={isRhsSwitching}
                    isWhatsApp={String(activeChannel).toLowerCase() === "whatsapp"}
                    showBackButton={isMobile}
                    onBackClick={() => setMobileMode("list")}
                    onSubjectUpdated={(next) => {
                        patchSelectedRow({ Subject: next });
                    }}
                    onStatusUpdated={(next) => {
                        const closeDate = String(next).toLowerCase() === "closed" ? new Date().toISOString() : null;
                        patchSelectedRow({ Status: next, CloseDate: closeDate });
                    }}
                    onContactLinked={(contactId, contactName) => {
                        patchSelectedRow({ ContactID: contactId, ContactName: contactName });
                        fetchChats(undefined);
                    }}
                    onAssigneeUpdated={({ kind, name }) => {
                        const patch = (kind === "Team")
                            ? { TeamAssigneeName: name, RepresentativeAssigneeName: null, RepresentativeAssigneeID: null }
                            : {
                                RepresentativeAssigneeName: name,
                                TeamAssigneeName: null,
                                RepresentativeAssigneeID:
                                    (currentUserName && name === currentUserName && currentRepId != null)
                                        ? Number(currentRepId) : selectedRef.current?.RepresentativeAssigneeID ?? null
                            };
                        patchSelectedRow(patch);
                        fetchChats(undefined);
                    }}
                    onLinkedJobsUpdated={(newLinks) => {
                        // Update the selected chat immediately
                        setSelected(s => (s ? { ...s, LinkedJobs: newLinks } : s));
                        // Keep the list in sync so when you navigate away/back, props are fresh
                        const id = selectedRef.current?.ChatID;
                        if (id) {
                            setChats(list => list.map(c => c.ChatID === id ? { ...c, LinkedJobs: newLinks } : c));
                        }
                    }}
                />
            </main>

            {/* Uniqueness rule popup */}
            <AccentPopup
                show={uniqPopup.show}
                anchor={uniqPopup.anchor ?? splitRef}
                onAutoClose={() => setUniqPopup({ show: false, anchor: null })}
                anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
                popupAlign={{ horizontal: 'center', vertical: 'top' }}
                headerContent={<strong>{t("application_strings.views.chat.uniqueness.title")}</strong>}
                actionsContent={<button className="btn btn-primary btn-sm ms-auto" onClick={() => setUniqPopup({ show: false, anchor: null })}>{t('ok')}</button>}
            >
                {t("application_strings.views.chat.uniqueness.body")}
            </AccentPopup>
        </div>
    );
}
